import '../portal/portalTheme.css';
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { BACKEND_URL } from '../../utils/backendURL';
import { Calendar, CheckCircle2, ArrowRight, Shield, ChevronDown, Loader2, Sparkles, Users, Zap, Lock } from 'lucide-react';

// Inline Lexa logo (same image as landing page header / Logo.js)
const FunnelLogo = () => (
  <a href="/" className="flex items-center gap-2 group" data-testid="funnel-logo">
    <img
      src="/assets/footer-icon.png"
      alt="Lexa.run"
      className="h-9 w-9 object-contain"
      onError={(e) => { e.currentTarget.style.display = 'none'; }}
    />
    <span
      className="text-xl font-bold bg-clip-text text-transparent"
      style={{ backgroundImage: 'linear-gradient(to right, #9333ea, #3b82f6)' }}
    >
      Lexa.run
    </span>
  </a>
);

/**
 * BookDemoFunnel, Funnel #2 (High-intent demo booking)
 * Path: /book-demo
 * Product walkthrough request with a qualifier form and common questions.
 */
export default function BookDemoFunnel() {
  const [form, setForm] = useState({
    name: '', email: '', company: '', role: '', team_size: '',
    app_url: '', monthly_test_volume: '', biggest_qa_pain: '', phone: '',
    preferred_time: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [openFaq, setOpenFaq] = useState(null);

  useEffect(() => {
    document.title = 'Request a Product Demo | Lexa.run';
  }, []);

  const getUtm = () => {
    const params = new URLSearchParams(window.location.search);
    return {
      utm_source: params.get('utm_source') || undefined,
      utm_campaign: params.get('utm_campaign') || undefined,
    };
  };

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.name.trim() || !form.email.trim() || !form.company.trim()) {
      setError('Please fill out your name, work email, and company.');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/funnel/demo`, {
        ...form,
        ...getUtm(),
      });
      setSubmitted(true);
      setTimeout(() => document.getElementById('thank-you-anchor')?.scrollIntoView({ behavior: 'smooth' }), 100);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const scrollToForm = () => {
    document.getElementById('demo-form')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setTimeout(() => document.getElementById('demo-name')?.focus(), 600);
  };

  return (
    <div className="lexa-portal lexa-portal-content min-h-screen antialiased" data-testid="funnel-book-demo">
      {/* Minimal top bar */}
      <header className="border-b border-slate-200 bg-white/80 backdrop-blur-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <FunnelLogo />
          <button onClick={scrollToForm} data-testid="header-cta" className="hidden sm:inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-purple-600 to-blue-600 px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-purple-500/30 hover:-translate-y-0.5 transition-all">
            Book My Demo <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </header>

      {/* Urgency strip */}
      <div className="bg-slate-950 text-white text-center py-2.5 text-sm font-medium">
        <Sparkles className="inline h-4 w-4 mr-1.5 -mt-0.5 text-amber-300" />
        Explore test automation with the people building Lexa.
      </div>

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <div className="absolute inset-0 bg-[radial-gradient(50%_60%_at_50%_0%,rgba(124,58,237,0.18),transparent_70%)]" />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 lg:py-20 grid lg:grid-cols-5 gap-10 items-start">
          {/* LEFT pitch */}
          <div className="lg:col-span-3">
            <div className="inline-flex items-center gap-2 rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-700 mb-5">
              <Calendar className="h-3.5 w-3.5" /> PRODUCT WALKTHROUGH
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-[64px] font-extrabold tracking-tight text-slate-900 leading-[1.04]">
              Explore <span className="text-violet-700">Lexa</span> with our team.
            </h1>
            <p className="mt-5 text-lg sm:text-xl text-slate-600 max-w-2xl">
              Tell us what you need to test. We can walk through the product and discuss how it could fit your workflow.
            </p>

            {/* What you'll get (value stack) */}
            <div className="mt-8 rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
              <div className="text-xs font-bold tracking-widest text-purple-700 mb-3">WHAT YOU'LL WALK AWAY WITH</div>
              <ul className="space-y-3.5">
                {[
                  ['Product walkthrough', 'explore API tests, browser flows, and results'],
                  ['Your testing requirements', 'discuss the scenarios that matter to your team'],
                  ['Plan questions', 'review current limits and available options'],
                ].map(([t, s], i) => (
                  <li key={i} className="flex items-start gap-3" data-testid={`demo-value-${i}`}>
                    <CheckCircle2 className="h-6 w-6 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <div><b className="text-slate-900">{t}</b> <span className="text-slate-600">· {s}</span></div>
                  </li>
                ))}
              </ul>

            </div>

          </div>

          {/* RIGHT form */}
          <div className="lg:col-span-2 lg:sticky lg:top-24">
            {!submitted ? (
              <form id="demo-form" onSubmit={handleSubmit} data-testid="demo-form" className="rounded-3xl border border-slate-200 bg-white p-7 shadow-2xl shadow-purple-500/10 relative overflow-hidden">
                <div className="absolute -top-24 -right-24 w-56 h-56 rounded-full bg-gradient-to-br from-purple-200/60 to-blue-200/60 blur-3xl pointer-events-none" />
                <div className="relative">
                  <div className="inline-flex items-center gap-1.5 rounded-full bg-amber-100 text-amber-800 px-2.5 py-1 text-[11px] font-bold mb-3">
                    <span className="inline-block w-1.5 h-1.5 bg-amber-500 rounded-full animate-pulse" />
                    REQUEST A DEMO
                  </div>
                  <h2 className="text-2xl font-extrabold text-slate-900 leading-tight">Request a Product Demo</h2>
                  <p className="text-sm text-slate-500 mt-1.5">Our team will follow up to arrange a time.</p>

                  <div className="mt-5 space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <input id="demo-name" data-testid="demo-form-name" required value={form.name} onChange={update('name')} placeholder="Full name *" className="rounded-xl border border-slate-200 px-4 py-3 text-sm focus:border-purple-500 focus:ring-2 focus:ring-purple-200 outline-none" autoComplete="name" />
                      <input data-testid="demo-form-email" required type="email" value={form.email} onChange={update('email')} placeholder="Work email *" className="rounded-xl border border-slate-200 px-4 py-3 text-sm focus:border-purple-500 focus:ring-2 focus:ring-purple-200 outline-none" autoComplete="email" />
                    </div>
                    <input data-testid="demo-form-company" required value={form.company} onChange={update('company')} placeholder="Company *" className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:border-purple-500 focus:ring-2 focus:ring-purple-200 outline-none" />
                    <div className="grid grid-cols-2 gap-3">
                      <select data-testid="demo-form-role" value={form.role} onChange={update('role')} className="rounded-xl border border-slate-200 px-3 py-3 text-sm focus:border-purple-500 focus:ring-2 focus:ring-purple-200 outline-none bg-white text-slate-700">
                        <option value="">Your role</option>
                        <option>QA Engineer / QA Lead</option>
                        <option>Engineering Manager / VP Eng</option>
                        <option>Founder / CTO</option>
                        <option>Product Manager</option>
                        <option>Developer</option>
                        <option>Other</option>
                      </select>
                      <select data-testid="demo-form-team" value={form.team_size} onChange={update('team_size')} className="rounded-xl border border-slate-200 px-3 py-3 text-sm focus:border-purple-500 focus:ring-2 focus:ring-purple-200 outline-none bg-white text-slate-700">
                        <option value="">Team size</option>
                        <option>1–5</option><option>6–20</option><option>21–50</option><option>51–200</option><option>200+</option>
                      </select>
                    </div>
                    <input data-testid="demo-form-url" type="url" value={form.app_url} onChange={update('app_url')} placeholder="App URL (we'll build tests live)" className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:border-purple-500 focus:ring-2 focus:ring-purple-200 outline-none" />
                    <select data-testid="demo-form-volume" value={form.monthly_test_volume} onChange={update('monthly_test_volume')} className="w-full rounded-xl border border-slate-200 px-3 py-3 text-sm focus:border-purple-500 focus:ring-2 focus:ring-purple-200 outline-none bg-white text-slate-700">
                      <option value="">Monthly test volume (approx.)</option>
                      <option>0 (we don't test yet)</option>
                      <option>1–100</option>
                      <option>100–1,000</option>
                      <option>1,000–10,000</option>
                      <option>10,000+</option>
                    </select>
                    <textarea data-testid="demo-form-pain" value={form.biggest_qa_pain} onChange={update('biggest_qa_pain')} placeholder="Biggest QA pain right now? (optional, helps us prep)" rows={2} className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:border-purple-500 focus:ring-2 focus:ring-purple-200 outline-none resize-none" />
                  </div>

                  {error && <p data-testid="demo-form-error" className="mt-3 text-sm text-rose-600">{error}</p>}

                  <button type="submit" disabled={submitting} data-testid="demo-form-submit" className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-purple-600 to-blue-600 px-6 py-4 text-base font-bold text-white shadow-lg shadow-purple-500/30 hover:shadow-purple-500/50 hover:-translate-y-0.5 transition-all disabled:opacity-60 disabled:cursor-not-allowed">
                    {submitting ? (<><Loader2 className="h-5 w-5 animate-spin" /> Sending your request…</>) : (<>Request My Demo <ArrowRight className="h-5 w-5" /></>)}
                  </button>
                  <p className="text-[11px] text-slate-400 mt-3 text-center leading-relaxed">
                    <Lock className="inline h-3 w-3 mr-1 -mt-0.5" />
                    No sales pitch. No spam. Your data stays private.
                  </p>
                </div>
              </form>
            ) : (
              <div id="thank-you-anchor" data-testid="demo-form-success" className="rounded-3xl border border-emerald-200 bg-gradient-to-br from-emerald-50 to-white p-8 shadow-2xl shadow-emerald-500/10 text-center">
                <div className="w-16 h-16 mx-auto rounded-full bg-emerald-500 flex items-center justify-center text-white mb-4">
                  <CheckCircle2 className="h-9 w-9" />
                </div>
                <h2 className="text-2xl font-extrabold text-slate-900">You're In. 🎉</h2>
                <p className="mt-3 text-slate-600 leading-relaxed">
                  We received your request. Our team will email you to arrange your demo.
                </p>
                <div className="mt-6 rounded-2xl bg-white border border-slate-200 p-4 text-left text-sm text-slate-600">
                  <div className="font-bold text-slate-900 mb-2">While you wait, bookmark this:</div>
                  <a href="/get-started" className="block text-purple-600 hover:underline">→ Explore the free plan</a>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Who this is for / not for (Hormozi signature qualifier) */}
      <section className="bg-white border-y border-slate-200 py-16 sm:py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 text-center tracking-tight">Is This Demo Right For You?</h2>
          <p className="mt-3 text-slate-600 text-center max-w-2xl mx-auto">Share your goals so we can focus the conversation on your testing workflow.</p>
          <div className="mt-10 grid md:grid-cols-2 gap-5">
            <div className="rounded-3xl border-2 border-emerald-200 bg-gradient-to-br from-emerald-50 to-white p-7">
              <div className="text-xs font-bold tracking-widest text-emerald-700">PERFECT FIT IF YOU ARE</div>
              <ul className="mt-4 space-y-3">
                {[
                  'A QA engineer / QA lead drowning in manual tests',
                  'An Engineering Manager / VP reviewing their testing workflow',
                  'A founder/CTO whose QA bottleneck is slowing shipping',
                  'Already using Playwright, Cypress, or Selenium and tired of maintenance',
                  'Looking to organize regression testing',
                ].map((t, i) => (<li key={i} className="flex items-start gap-2 text-slate-700"><CheckCircle2 className="h-5 w-5 text-emerald-500 flex-shrink-0 mt-0.5" />{t}</li>))}
              </ul>
            </div>
            <div className="rounded-3xl border-2 border-rose-100 bg-rose-50/40 p-7">
              <div className="text-xs font-bold tracking-widest text-rose-600">NOT A FIT IF YOU ARE</div>
              <ul className="mt-4 space-y-3">
                {[
                  'Just shopping for a tool with no real QA pain',
                  'Looking for a free trial only, start at /get-started instead',
                  'Building a desktop-only app (we focus on browser flows and API testing)',
                  'Looking for a managed testing service',
                ].map((t, i) => (<li key={i} className="flex items-start gap-2 text-slate-700"><span className="text-rose-400 font-bold flex-shrink-0">✕</span>{t}</li>))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 text-center tracking-tight">Demo FAQs</h2>
          <div className="mt-10 space-y-3">
            {[
              { q: 'How is the demo arranged?', a: 'Submit your request and our team will follow up to arrange a time and agenda.' },
              { q: 'What should I share?', a: 'Tell us about your application and testing requirements. You do not need to share private credentials.' },
              { q: 'Can I explore the product first?', a: 'Yes. You can sign up for the free plan without booking a demo. See Pricing for current limits.' },
            ].map((item, i) => (
              <div key={i} className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
                <button onClick={() => setOpenFaq(openFaq === i ? null : i)} data-testid={`demo-faq-toggle-${i}`} className="w-full flex items-center justify-between text-left px-5 py-4 font-semibold text-slate-900 hover:bg-slate-50 transition">
                  <span>{item.q}</span>
                  <ChevronDown className={`h-5 w-5 text-slate-400 transition-transform ${openFaq === i ? 'rotate-180' : ''}`} />
                </button>
                {openFaq === i && <div className="px-5 pb-4 text-slate-600 leading-relaxed">{item.a}</div>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-20 bg-gradient-to-br from-purple-600 via-violet-600 to-blue-600 text-white text-center">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-5xl font-extrabold tracking-tight leading-tight">
            A product walkthrough. <br className="hidden sm:block" /> Your testing questions.
          </h2>
          <p className="mt-4 text-purple-100 text-lg">See whether Lexa fits your testing workflow.</p>
          <button onClick={scrollToForm} data-testid="final-cta" className="mt-8 inline-flex items-center gap-2 rounded-full bg-white text-purple-700 px-8 py-4 text-base font-bold shadow-xl hover:-translate-y-0.5 hover:shadow-2xl transition-all">
            Request My Demo <ArrowRight className="h-5 w-5" />
          </button>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-6 text-sm text-purple-100">
            <span className="inline-flex items-center gap-1.5"><Shield className="h-4 w-4" /> Arranged with you</span>
            <span className="inline-flex items-center gap-1.5"><Users className="h-4 w-4" /> Senior strategist, not sales</span>
            <span className="inline-flex items-center gap-1.5"><Zap className="h-4 w-4" /> Explore real workflows</span>
          </div>
        </div>
      </section>

      <footer className="bg-slate-950 text-slate-400 text-xs py-6 text-center px-4">
        © {new Date().getFullYear()} Lexa.run · <a href="/privacy" className="hover:text-white">Privacy</a> · <a href="/terms" className="hover:text-white">Terms</a>
      </footer>
    </div>
  );
}
